import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

const ParentMessaging = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation('parentmessaging');
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedParents, setSelectedParents] = useState([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentStatus, setSentStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    const fetchParents = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/parents`);
        setParents(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchFailed'));
        setLoading(false);
        console.error('Error fetching parents:', err);
      }
    };

    fetchParents();
  }, [api, user.school_reg, t]);

  const handleSelectParent = (parent) => {
    setSelectedParents(prev => {
      const isSelected = prev.some(p => p.id === parent.id);
      if (isSelected) {
        return prev.filter(p => p.id !== parent.id);
      } else {
        return [...prev, parent];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedParents.length === filteredParents.length) {
      setSelectedParents([]);
    } else {
      setSelectedParents([...filteredParents]);
    }
  };

  const sendSms = async () => {
    if (selectedParents.length === 0 || !message.trim()) {
      setSentStatus({ success: false, message: t('status.validation') });
      return;
    }

    setIsSending(true);
    setSentStatus(null);
    
    try {
      const results = await Promise.all(
        selectedParents.map(async (parent) => {
          const studentName = parent.student_name || "your child"; // Assuming parent has student_name property or fallback
          
          const payload = {
            parent_phone_number: parent.phone,
            message: message.replace('{student_name}', studentName),
            student_name: studentName,
            context: ''
          };
          
          return await api.post("/api/v1/sms/send-to-parent", payload);
        })
      );
      
      setIsSending(false);
      setSentStatus({ 
        success: true, 
        message: t('status.success', { count: selectedParents.length }) 
      });
      
      // Clear selections and message after successful send
      setSelectedParents([]);
      setMessage('');
      
    } catch (err) {
      setIsSending(false);
      setSentStatus({ 
        success: false, 
        message: t('status.error', { error: err.message }) 
      });
      console.error('Error sending SMS:', err);
    }
  };

  const filteredParents = parents.filter(parent => {
    return (
      parent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      parent.phone.includes(searchTerm) ||
      (parent.email && parent.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('pageTitle')}</h2>
      
      {/* Status message */}
      {sentStatus && (
        <div className={`mb-4 p-4 rounded-md ${sentStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {sentStatus.message}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side - Parent selection */}
        <div className="lg:col-span-1 border-r border-gray-200 pr-4">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-700">{t('parents.title', { count: filteredParents.length })}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="flex items-center mb-4">
              <input
                type="text"
                placeholder={t('parents.search')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="selectAll"
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={selectedParents.length === filteredParents.length && filteredParents.length > 0}
                  onChange={handleSelectAll}
                />
                <label htmlFor="selectAll" className="text-sm font-medium text-gray-700">{t('parents.selectAll')}</label>
              </div>
              <span className="text-sm text-gray-500">
                {t('parents.selected', { selected: selectedParents.length, total: filteredParents.length })}
              </span>
            </div>
          </div>
          
          <div className={`overflow-y-auto max-h-96 pr-2 ${viewMode === 'grid' ? 'grid grid-cols-1 gap-2' : ''}`}>
            {filteredParents.length > 0 ? (
              filteredParents.map(parent => (
                <div
                  key={parent.id}
                  onClick={() => handleSelectParent(parent)}
                  className={`cursor-pointer border rounded-lg p-3 mb-2 transition-all duration-200 ${
                    selectedParents.some(p => p.id === parent.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedParents.some(p => p.id === parent.id)}
                      onChange={() => {}} // Handled by parent div click
                      className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{parent.name}</h4>
                      <div className="text-sm text-gray-500 mt-1">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {parent.phone}
                        </div>
                        {parent.email && (
                          <div className="flex items-center mt-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {parent.email}
                          </div>
                        )}
                        <div className="mt-1 text-xs inline-block bg-gray-100 rounded px-2 py-1">
                          {t('parents.relation', { type: parent.relation_type })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                {t('parents.noResults')}
              </div>
            )}
          </div>
        </div>
        
        {/* Right side - Message composition */}
        <div className="lg:col-span-2">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('compose.title')}</h3>
            
            <div className="mb-4">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                {t('compose.messageContent')}
              </label>
              <div className="relative">
                <textarea
                  id="message"
                  rows={8}
                  placeholder={t('compose.placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  dir="auto"
                ></textarea>
                <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                  {t('compose.stats', { characters: message.length, sms: Math.ceil(message.length / 160) })}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('compose.tip')}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="text-sm text-gray-600 mb-2 sm:mb-0">
                {t('compose.sendingTo', { count: selectedParents.length })}
              </div>
              <button
                onClick={sendSms}
                disabled={isSending || selectedParents.length === 0 || !message.trim()}
                className={`px-6 py-2 rounded-md text-white font-medium flex items-center ${
                  isSending || selectedParents.length === 0 || !message.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('buttons.sending')}
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {t('buttons.sendSMS')}
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Selected Parents Summary */}
          {selectedParents.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('selectedParents.title')}</h3>
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex flex-wrap gap-2">
                  {selectedParents.map(parent => (
                    <div key={parent.id} className="bg-white border border-gray-300 rounded-full px-3 py-1 flex items-center">
                      <span className="text-sm">{parent.name}</span>
                      <button
                        onClick={() => handleSelectParent(parent)}
                        className="ml-2 text-gray-500 hover:text-red-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentMessaging;